import { fetchAuthSession } from 'aws-amplify/auth'

type GraphQLErrorResponse = {
  message?: string
  extensions?: {
    code?: string
  }
}

interface GraphQLRequestDocument {
  toString(): string
}

export class GraphQLRequestError extends Error {
  extensions?: GraphQLErrorResponse['extensions']

  constructor(error: GraphQLErrorResponse) {
    super(error.message || 'Error...')
    this.name = 'GraphQLRequestError'
    this.extensions = error.extensions
  }
}

export const getGraphQLErrorCode = (error: unknown): string | undefined => {
  if (!(error instanceof GraphQLRequestError)) {
    return undefined
  }

  return error.extensions?.code
}

export const isTerminalGraphQLError = (error: unknown): boolean => {
  const errorCode = getGraphQLErrorCode(error)

  return errorCode === '401' || errorCode === '403' || errorCode === '404'
}

export const shouldRetryGraphQLRequest = (
  failureCount: number,
  error: unknown
): boolean => {
  if (isTerminalGraphQLError(error)) {
    return false
  }

  return failureCount < 3
}

const REVIEWER_AUTH_SCHEME_PREFIX = 'Reviewer '
const REVIEWER_TOKEN_QUERY_PARAM = 'token'

const getReviewerTokenFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null

  const token = new URLSearchParams(window.location.search)
    .get(REVIEWER_TOKEN_QUERY_PARAM)
    ?.trim()

  return token ? token : null
}

export const fetchData = async <TData, TVariables>(
  query: string | GraphQLRequestDocument,
  variables?: TVariables,
  options?: RequestInit['headers']
): Promise<TData> => {
  const headers: Record<string, string> = {}
  const reviewerToken = getReviewerTokenFromUrl()

  if (reviewerToken) {
    headers.Authorization = `${REVIEWER_AUTH_SCHEME_PREFIX}${reviewerToken}`
  } else {
    try {
      const { tokens } = await fetchAuthSession()
      if (!tokens) throw new Error('No access token')
      headers.Authorization = tokens.accessToken.toString()
    } catch (error) {
      console.error(error)
      headers.Authorization = 'none'
    }
  }

  const res = await fetch(`${import.meta.env.VITE_API_URL}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...options,
    },
    body: JSON.stringify({
      query: query.toString(),
      variables,
    }),
  })

  const json = await res.json()

  if (json.errors) {
    const error = json.errors[0] || {}
    console.error(error)
    throw new GraphQLRequestError(error)
  }

  return json.data
}

export const fetchGenerated = <TData, TVariables>(
  query: string | GraphQLRequestDocument,
  variables?: TVariables,
  options?: RequestInit['headers']
): (() => Promise<TData>) => {
  return () => fetchData(query, variables, options)
}
